/**
 * The schedule on a source, and the gate in front of it.
 *
 * Three things this panel is careful about.
 *
 * **Nobody is asked to write a cron expression.** The people who configure a
 * source are not the people who remember that the fifth field is day-of-week,
 * so the panel asks how often, on what day and at what time, and `schedule-plan`
 * assembles the expression. The expression is still shown — it is what the
 * server stores and what an operator would recognise — but it is an output,
 * displayed rather than typed.
 *
 * **A schedule cannot be switched on until a dry run has passed.** So the
 * switch is not offered until the gate says ready — the checks are shown either
 * way, because "why not" is more use than a disabled button.
 *
 * **What a schedule does not promise is stated here rather than discovered.**
 * The limitations are rendered from the payload rather than written as copy, so
 * a constraint the server knows about cannot be one the screen forgot.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  ApiError,
  api,
  type Gate,
  type GateCode,
  type Schedule,
  type SourceProfile,
} from "../api";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  Field,
  FieldSet,
  KeyValue,
  Muted,
  PillGroup,
  SectionTitle,
  Select,
  TextInput,
} from "../components/ui";
import { formatRelative, formatTimestamp, humaniseCode } from "../format";
import {
  DEFAULT_PLAN,
  RECURRENCE_LABELS,
  WEEKDAYS,
  describe,
  fromCron,
  fromTimeValue,
  localTimeHint,
  ordinal,
  toCron,
  toTimeValue,
  type Recurrence,
  type SchedulePlan,
} from "../schedule-plan";

const RECURRENCES: { value: Recurrence; label: string }[] = (
  ["hourly", "daily", "weekly", "monthly"] as Recurrence[]
).map((value) => ({ value, label: RECURRENCE_LABELS[value] }));

/** Minutes past the hour, offered in fives. An hourly Run at :37 is a typo. */
const MINUTES_PAST = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

/**
 * The headline over the gate's reason. Short, because the Banner's title is a
 * headline and the reason itself is rendered underneath it in full — saying the
 * same sentence twice is what this replaced.
 */
/**
 * The headline over the gate's reason, one per refusal the server names.
 *
 * Keyed on `gate.code` rather than on the wording of `reason`, which is prose
 * the server is free to reword, and no longer inferred from which fields the
 * payload populates. That inference held while three refusals were
 * distinguishable by shape and broke on the fourth: a roster that turned over
 * carries a dry run whose own checks all passed, because the turnover was
 * measured by the Run that published rather than by the dry run — and it was
 * headlined "did not pass every check" over a dry run that had.
 */
const GATE_HEADLINES: Record<GateCode, string> = {
  ready: "A dry run has passed for this configuration",
  no_dry_run: "This source has not had a dry run",
  dry_run_retired: "The last dry run no longer describes this source",
  checks_failed: "The last dry run did not pass every check",
  roster_turned_over: "The players changed under the season this source declares",
};

function gateHeadline(gate: Gate): string {
  // Falls back rather than rendering `undefined`: a server that names a
  // refusal this build has never heard of is still refusing, and the reason
  // underneath says what it is.
  return GATE_HEADLINES[gate.code] ?? "This source cannot be put on a schedule yet";
}

const OUTCOME_TONE = {
  passed: "success",
  failed: "danger",
  not_measured: "warning",
} as const;

export function SchedulePanel({
  profile,
  onChanged,
}: {
  profile: SourceProfile;
  onChanged: () => void;
}) {
  const schedule = profile.schedule;
  const saved = schedule?.cron ?? null;

  const [plan, setPlan] = useState<SchedulePlan>(() => fromCron(saved) ?? DEFAULT_PLAN);

  /**
   * Whether the picker is standing aside for an expression it cannot draw.
   *
   * A schedule that fires every fifteen minutes is a real, firing schedule
   * that no arrangement of these four controls describes. Rather than round it
   * to the nearest thing the form can say — which would change when a source
   * runs without anybody asking for that — the panel shows it as it stands and
   * waits to be told to replace it.
   */
  const [advanced, setAdvanced] = useState(() => saved !== null && fromCron(saved) === null);

  // The profile is polled, so a schedule the timer switched off — because the
  // configuration changed under it — has to reach the controls too.
  useEffect(() => {
    const parsed = fromCron(saved);
    setPlan(parsed ?? DEFAULT_PLAN);
    setAdvanced(saved !== null && parsed === null);
  }, [saved]);

  const cron = useMemo(() => toCron(plan), [plan]);

  /**
   * What the last Save did, reported here rather than at the top of the screen.
   *
   * The panel used to hand its outcome to the screen, which renders one Banner
   * above every card. This card sits far enough down that the answer landed off
   * the top of the viewport: pressing Save looked like pressing nothing, which
   * for a control that decides whether a source publishes unattended is the
   * worst possible ambiguity. The outcome now appears beside the button that
   * caused it, and only there — the same sentence in two places on one screen
   * reads as two things having happened.
   */
  const [outcome, setOutcome] = useState<{ tone: "success" | "danger"; message: string } | null>(
    null,
  );

  /**
   * Every edit clears the last outcome.
   *
   * "Switched on" sitting beside controls that have since been changed reads as
   * though the change is live, which is the one thing this panel must never
   * imply. The refetch after a save goes through the effect above instead, so a
   * success message survives its own reload.
   */
  const editPlan = (change: (current: SchedulePlan) => SchedulePlan) => {
    setOutcome(null);
    setPlan(change);
  };

  const save = useMutation({
    mutationFn: (enabled: boolean) => api.setSchedule(profile.id, { cron, enabled }),
    onSuccess: (updated: Schedule) => {
      setOutcome({
        tone: "success",
        message: updated.enabled
          ? `Switched on. The next Run is ${formatTimestamp(updated.next_run_at)}.`
          : "Saved, and switched off. Nothing runs until you switch it on.",
      });
      onChanged();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError ? error.message : "Could not save that schedule.";
      setOutcome({ tone: "danger", message });
    },
  });

  if (!schedule) return null;
  const gate = schedule.gate;
  const dirty = saved !== cron;
  // `variables` is what the in-flight mutation was called with: true for
  // "Switch on", false for "Save". It is what lets one spinner label the button
  // that is actually working rather than both of them.
  const pendingEnable = save.isPending && save.variables === true;
  const hourly = plan.recurrence === "hourly";
  const localHint = hourly ? null : localTimeHint(plan.hour, plan.minute);

  return (
    <Card
      title="Schedule"
      description="When this source runs on its own. Times are UTC, and Runs queue behind one another, one at a time."
      actions={
        schedule.enabled ? (
          <Badge tone="success">on</Badge>
        ) : (
          <Badge>off</Badge>
        )
      }
    >
      <div className="flex flex-col gap-md">
        {advanced ? (
          /* An expression from outside the picker's vocabulary. Shown, not
             translated, and replaced only when somebody asks. */
          <Banner tone="info" title="This schedule was written as a cron expression">
            <div className="flex flex-col items-start gap-xs">
              <span>
                <Code>{saved}</Code> does something the simple picker cannot describe, so it
                is left exactly as it is. Nothing here changes until you replace it.
              </span>
              <Button size="sm" onClick={() => setAdvanced(false)}>
                Replace with a simple schedule
              </Button>
            </div>
          </Banner>
        ) : (
          <>
            <FieldSet
              label="How often should this source run?"
              hint="Pick a rhythm first — the rest of the questions follow from it."
            >
              <PillGroup
                value={plan.recurrence}
                options={RECURRENCES}
                onChange={(recurrence) => editPlan((current) => ({ ...current, recurrence }))}
              />
            </FieldSet>

            <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
              {plan.recurrence === "weekly" && (
                <Field label="On which day?">
                  <Select
                    value={String(plan.weekday)}
                    onChange={(event) =>
                      editPlan((current) => ({ ...current, weekday: Number(event.target.value) }))
                    }
                  >
                    {WEEKDAYS.map((day, index) => (
                      <option key={day} value={index}>
                        {day}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {plan.recurrence === "monthly" && (
                <Field
                  label="On which date?"
                  hint={
                    plan.monthDay > 28
                      ? "Months without this date are skipped — February always is."
                      : undefined
                  }
                >
                  <Select
                    value={String(plan.monthDay)}
                    onChange={(event) =>
                      editPlan((current) => ({ ...current, monthDay: Number(event.target.value) }))
                    }
                  >
                    {MONTH_DAYS.map((day) => (
                      <option key={day} value={day}>
                        {ordinal(day)}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {hourly ? (
                <Field label="At how many minutes past the hour?">
                  <Select
                    value={String(plan.minute)}
                    onChange={(event) =>
                      editPlan((current) => ({ ...current, minute: Number(event.target.value) }))
                    }
                  >
                    {/* A minute saved elsewhere may not be one of the fives, and
                        dropping it would silently move the schedule. */}
                    {(MINUTES_PAST.includes(plan.minute)
                      ? MINUTES_PAST
                      : [...MINUTES_PAST, plan.minute].sort((a, b) => a - b)
                    ).map((minute) => (
                      <option key={minute} value={minute}>
                        {minute === 0 ? "On the hour" : `${minute} minutes past`}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field
                  label="At what time (UTC)?"
                  hint={localHint ? `That is ${localHint}.` : undefined}
                >
                  <TextInput
                    type="time"
                    value={toTimeValue(plan)}
                    onChange={(event) => {
                      const time = fromTimeValue(event.target.value);
                      if (time) editPlan((current) => ({ ...current, ...time }));
                    }}
                  />
                </Field>
              )}
            </div>

            {/* What was chosen, said back as a sentence, with the expression it
                compiles to beside it. The sentence is for the person setting
                this up; the expression is for whoever has to recognise it in a
                log or a database row later. */}
            <div className="flex flex-wrap items-baseline gap-xs rounded-md bg-surface-soft p-md">
              <span className="text-body-sm-medium">{describe(plan)}</span>
              <Muted className="text-caption">
                Cron <Code className="code-sm">{cron}</Code>
              </Muted>
            </div>
          </>
        )}

        {!advanced && (
          <div className="flex flex-col gap-sm">
            <div className="flex flex-wrap items-center gap-sm">
              {/* Both buttons say what they are doing while they do it. A save
                  that crosses the network with the label unchanged is the same
                  as no feedback at all. */}
              <Button onClick={() => save.mutate(false)} disabled={save.isPending}>
                {save.isPending && !pendingEnable
                  ? "Saving…"
                  : schedule.enabled
                    ? "Save and switch off"
                    : "Save"}
              </Button>
              <Button
                variant="primary"
                onClick={() => save.mutate(true)}
                disabled={save.isPending || !gate.ready || (schedule.enabled && !dirty)}
                title={gate.ready ? undefined : gate.reason}
              >
                {save.isPending && pendingEnable
                  ? "Switching on…"
                  : schedule.enabled
                    ? "Re-enable"
                    : "Switch on"}
              </Button>
              {/* Why the primary button is dead, next to the button rather than
                  in a tooltip nobody hovers. The gate's full reason is in the
                  Banner below; this is the one-line version. */}
              {!gate.ready && !save.isPending && (
                <Muted className="text-caption">A dry run has to pass first.</Muted>
              )}
              {gate.ready && schedule.enabled && !dirty && !save.isPending && (
                <Muted className="text-caption">Change something to save again.</Muted>
              )}
            </div>

            {/* The answer, where the question was asked. */}
            {outcome && (
              <Banner
                tone={outcome.tone}
                title={outcome.tone === "success" ? outcome.message : "That schedule was not saved"}
              >
                {outcome.tone === "danger" && <span>{outcome.message}</span>}
              </Banner>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-md fold:grid-cols-3">
          {/* The server computes this whenever a cron is set, enabled or not,
              so somebody can see what an expression means before promoting it.
              Saving now always stores a cron — the picker cannot produce an
              empty one — so a switched-off schedule would otherwise show a time
              it has no intention of keeping. The qualifier is what keeps the
              cell from reading as a promise. */}
          <KeyValue label="Next Run">
            {schedule.cron ? (
              <>
                {formatTimestamp(schedule.next_run_at)}
                {!schedule.enabled && <Muted> if switched on</Muted>}
              </>
            ) : (
              <Muted>—</Muted>
            )}
          </KeyValue>
          <KeyValue label="Last fired">
            {schedule.last_fired_at ? formatRelative(schedule.last_fired_at) : <Muted>Never</Muted>}
          </KeyValue>
          <KeyValue label="Switched on by">
            {schedule.enabled_by ? (
              <>
                {schedule.enabled_by} <Muted>{formatRelative(schedule.enabled_at)}</Muted>
              </>
            ) : (
              <Muted>Nobody</Muted>
            )}
          </KeyValue>
        </div>

        {/* The gate. Shown whether it passes or not: an operator staring at a
            disabled button needs the reason more than the button. */}
        <Banner
          tone={gate.ready ? "success" : "warning"}
          title={
            gate.ready
              ? "A dry run has passed every gate check for this configuration"
              : gateHeadline(gate)
          }
        >
          <div className="flex flex-col gap-xs">
            {/* The reason in full, once. The title is the headline version of
                it; repeating the sentence underneath was the first thing this
                panel got wrong. */}
            {!gate.ready && <span>{gate.reason}</span>}
            {gate.dry_run_id && (
              <div>
                <Link to={`/runs/${gate.dry_run_id}`}>
                  Dry run from {formatRelative(gate.dry_run_at)}
                </Link>
              </div>
            )}
            {gate.checks.length > 0 && (
              <ul className="m-0 flex list-none flex-col gap-xxs p-0">
                {/* The badge holds the left edge and the text flows beside it.
                    Laid out as one wrapping row, a long detail broke the line
                    between the outcome and the check it belonged to. */}
                {gate.checks.map((check) => (
                  <li key={check.name} className="flex items-baseline gap-xs">
                    <span className="shrink-0">
                      <Badge tone={OUTCOME_TONE[check.outcome]}>
                        {humaniseCode(check.outcome)}
                      </Badge>
                    </span>
                    <span className="min-w-0">
                      <span className="text-body-sm-medium">{humaniseCode(check.name)}</span>{" "}
                      <Muted>{check.detail}</Muted>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Banner>

        {/* Rendered from the payload rather than written here, so a constraint
            the server knows about cannot be one this screen forgot. */}
        <div className="rounded-md bg-surface-soft p-md">
          <SectionTitle className="mb-xs">What a schedule does not promise</SectionTitle>
          <ul className="m-0 flex list-disc flex-col gap-xxs pl-md">
            {schedule.limitations.map((limitation) => (
              <li key={limitation.code} className="text-body-sm text-muted">
                {limitation.message}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}
